/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   Favorite
 */

import { useColorModeValue, useTheme } from '@chakra-ui/react'
import { Button } from '@zcode-ui/controls'
import { Ribbon } from '@zcode-ui/icons'
import React, { useCallback, useState } from 'react'
import { twJoin } from 'tailwind-merge'

/**
 * If given a dot-separated `color` from our theme (like `gray.300`), returns the
 * "real" color associated with that name (e.g. `#CECBC8`). Otherwise, returns the
 * given color as-is.
 *
 * This allows theme colors to be used with components (like `@zcode-ui/icons`'s
 * `Ribbon`) that don't have direct support for them.
 *
 * @param color a color to lookup in the theme
 * @param colors the colors from our theme
 */
const resolveColor = (color: string | undefined, colors: any) => {
  const match = color?.match(/^([a-z]+)\.(\d+)$/)
  if (match) {
    return colors[match[1]]?.[match[2]] ?? color
  }
  return color
}

export interface FavoriteProps {
  borderStyle?: 'none' | 'solid'
  className?: string
  disabled?: boolean
  fill?: string
  fillFavorite?: string
  fillOpacity?: number
  fillOpacityFavorite?: number
  isFavorite?: boolean
  isMobile?: boolean
  isRoundButton?: boolean
  label: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  onToggle?: (isFavorite: boolean) => void
  shouldUseTransparentBg?: boolean
  size?: number
  stroke?: string
  strokeFavorite?: string
  strokeHover?: string
  text?: React.ReactNode
}

export const Favorite = ({
  borderStyle = 'solid',
  className,
  disabled,
  fill,
  fillFavorite,
  fillOpacity = 1,
  fillOpacityFavorite = 1,
  isFavorite,
  isMobile,
  isRoundButton,
  label,
  onClick,
  onToggle,
  shouldUseTransparentBg,
  size,
  stroke,
  strokeFavorite,
  strokeHover,
  text,
}: FavoriteProps) => {
  const [hovered, setHovered] = useState(false)

  const onMouseOver = useCallback(() => !isMobile && setHovered(true), [isMobile])
  const onMouseOut = useCallback(() => !isMobile && setHovered(false), [isMobile])

  const { colors } = useTheme()
  const iconColor = useColorModeValue(colors.gray['600'], colors.gray['300'])
  const iconColorHover = useColorModeValue(colors.gray['800'], colors.gray['100'])

  const defaultStroke = resolveColor(stroke, colors) ?? iconColor
  const hoverStroke = resolveColor(strokeHover, colors) ?? iconColorHover
  const favoriteStroke = resolveColor(strokeFavorite, colors) ?? colors.pink['650']
  const favoriteFill = resolveColor(fillFavorite, colors) ?? colors.pink['650']
  const defaultFill = resolveColor(fill, colors) ?? 'transparent'

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick(e)
    if (onToggle && typeof onToggle === 'function') {
      onToggle(!isFavorite)
    }
  }

  return (
    <Button
      aria-label={label}
      aria-pressed={isFavorite}
      className={twJoin(
        shouldUseTransparentBg
          ? 'bg-transparent hover:bg-transparent hover:disabled:bg-transparent'
          : 'bg-gray-0 hover:bg-gray-0 hover:disabled:bg-gray-0',
        'border-gray-150 border sm:border-0 relative rounded-full size-8',
        !isRoundButton && 'sm:rounded-none',
        borderStyle === 'none' ? 'border-none' : 'border-solid',
        className
      )}
      disabled={disabled}
      onBlur={onMouseOut}
      onClick={handleOnClick}
      onFocus={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
    >
      <div className="absolute top-1/2 right-1/2 flex items-center translate-x-1/2 -translate-y-1/2 [&>svg>path]:stroke-[2]">
        <Ribbon
          fill={isFavorite ? favoriteFill : defaultFill}
          fillOpacity={isFavorite ? String(fillOpacityFavorite) : String(fillOpacity)}
          size={size ?? (isMobile ? 16 : 20)}
          stroke={hovered ? hoverStroke : isFavorite ? favoriteStroke : defaultStroke}
        />
        {text && <div className="ml-2">{text}</div>}
      </div>
    </Button>
  )
}
